/**
 * Initial Data API Service
 * Handles fetching initial data required for admin web startup
 */

import { useQuery } from '@tanstack/react-query';
import { apiGet } from './api';

// Type definitions matching backend schema
export interface Language {
  code: string;
  name: string;
  native_name: string;
  supported_as_native: boolean;
  supported_as_target: boolean;
  sort_order: number | null;
  narrator_voice_id: string | null;
}

export interface Locale {
  code: string;
  language_code: string;
  name: string;
}

export interface SkillLevelLocalization {
  language_code: string;
  localized_name: string;
  localized_description?: string;
}

export interface SkillLevel {
  id: number;
  cefr_code: string;
  localizations: SkillLevelLocalization[];
}

/**
 * Get English name from skill level localizations
 * Falls back to first available name or level ID if no localization found
 */
export function getSkillLevelEnglishName(skillLevel: SkillLevel): string {
  const englishLoc = skillLevel.localizations.find((loc) => loc.language_code === 'en');
  if (englishLoc) return englishLoc.localized_name;
  if (skillLevel.localizations.length > 0) return skillLevel.localizations[0].localized_name;
  return `Level ${skillLevel.id}`;
}

/**
 * Get English description from skill level localizations
 */
export function getSkillLevelEnglishDescription(skillLevel: SkillLevel): string | undefined {
  const englishLoc = skillLevel.localizations.find((loc) => loc.language_code === 'en');
  if (englishLoc) return englishLoc.localized_description;
  if (skillLevel.localizations.length > 0) return skillLevel.localizations[0].localized_description;
  return undefined;
}

export interface TagLocalization {
  language_code: string;
  localized_name: string;
}

// Story 15.8: Tag IDs are now integers
export interface Tag {
  id: number;
  learning_goal_ids: number[];
  localizations: TagLocalization[];
}

/**
 * Get English name from tag localizations
 * Falls back to first available name or tag ID if no localization found
 */
export function getTagEnglishName(tag: Tag): string {
  const englishLoc = tag.localizations.find((loc) => loc.language_code === 'en');
  if (englishLoc) return englishLoc.localized_name;
  if (tag.localizations.length > 0) return tag.localizations[0].localized_name;
  return `Tag ${tag.id}`;
}

export interface LearningGoalLocalization {
  language_code: string;
  localized_name: string;
}

// Story 15.8: Learning goal IDs are now integers
export interface LearningGoal {
  id: number;
  name: string;
  icon_emoji?: string;
  display_order: number;
  category?: string;
  color?: string;
  color_light?: string;
  localizations: LearningGoalLocalization[];
}

/**
 * Get English name from learning goal localizations
 * Falls back to first available name or goal name if no localization found
 */
export function getLearningGoalEnglishName(goal: LearningGoal): string {
  const englishLoc = goal.localizations.find((loc) => loc.language_code === 'en');
  if (englishLoc) return englishLoc.localized_name;
  if (goal.localizations.length > 0) return goal.localizations[0].localized_name;
  return goal.name;
}

// Story 15.11: Categorical duration types
// Note: Backend returns localizations as dict (keyed by language_code)
// IDs: 1=micro, 2=short, 3=medium, 4=long, 5=extended
export interface EpisodeDurationLocalization {
  name: string;
  description: string;
}

export interface EpisodeDuration {
  id: number;
  max_turns: number;
  display_order: number;
  localizations: Record<string, EpisodeDurationLocalization>;
}

/**
 * Get English name from episode duration localizations
 * Falls back to first available name or duration ID if no localization found
 */
export function getEpisodeDurationEnglishName(duration: EpisodeDuration): string {
  if (duration.localizations['en']) return duration.localizations['en'].name;
  const firstLoc = Object.values(duration.localizations)[0];
  if (firstLoc) return firstLoc.name;
  return `Duration ${duration.id}`;
}

export interface AdminInitialData {
  languages: Language[];
  locales: Locale[];
  skill_levels: SkillLevel[];
  tags: Tag[];
  learning_goals: LearningGoal[];
  episode_durations: EpisodeDuration[]; // Story 15.11
}

// Query key factory for type safety and cache management
export const initialDataKeys = {
  all: ['initialData'] as const,
  admin: () => [...initialDataKeys.all, 'admin'] as const,
};

// Fetcher function
const fetchAdminInitialData = async (): Promise<AdminInitialData> => {
  return apiGet<AdminInitialData>('/api/admin/initial-data');
};

// React Query hook
export const useAdminInitialData = () => {
  return useQuery({
    queryKey: initialDataKeys.admin(),
    queryFn: fetchAdminInitialData,
    staleTime: Infinity, // Initial data never goes stale during session
    gcTime: Infinity, // Keep in cache indefinitely
    retry: 3, // Retry failed requests up to 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
